import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../utils/supabase.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Alert,
} from "reactstrap";
// core components
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { serverHost } from "variables/serverdata.js";

function CompanyProfile() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState({
    company_name: "",
    address: "",
    city: "",
    country: "",
    postal_code: "",
    email: "",
    created_at: "",
    dealer_uuid: null,
    client_uuid: null,
    photo: null,
  });
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState({
    show: false,
    success: false,
    message: "",
  });
  const [isFromClientsList, setIsFromClientsList] = useState(false);
  const [isNewClient, setIsNewClient] = useState(false);
  const [allModules, setAllModules] = useState([]);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    if (currentUser) {
      setUserRole(currentUser.user_role || "");
    }
    fetchCompanyData();
  }, []);

  // Handle file input change
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
    }
  };

  // Upload photo file to backend
  const uploadPhoto = async () => {
    if (!selectedFile) {
      alert("Please select a file first");
      return;
    }
    const formData = new FormData();
    formData.append("photo", selectedFile);

    try {
      const response = await fetch("http://localhost:3030/upload-photo", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Upload failed");
      }
      const data = await response.json();
      setPhotoUrl(data.path);
      setSaveStatus({
        show: true,
        success: true,
        message: "Photo uploaded successfully",
      });

      // Update photo filename in database for current client
      if (companyData.client_uuid) {
        const { error } = await supabase
          .from("client")
          .update({ photo: data.path })
          .eq("client_uuid", companyData.client_uuid);

        if (error) {
          setSaveStatus({
            show: true,
            success: false,
            message: "Failed to update photo in database: " + error.message,
          });
          return;
        }

        // Update local companyData state
        setCompanyData((prev) => ({ ...prev, photo: data.path }));
      }
    } catch (error) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Photo upload failed: " + error.message,
      });
    }
  };

  // Fetch all modules on mount
  useEffect(() => {
    const fetchAllModules = async () => {
      try {
        const { data, error } = await supabase.from("module").select("*");

        if (error) {
          console.error("Error fetching all modules:", error);
          return;
        }
        console.log(data);
        setAllModules(data || []);
      } catch (error) {
        console.error("Error fetching all modules:", error);
      }
    };

    fetchAllModules();
  }, []);

  // Fetch client assigned modules when client_uuid changes
  useEffect(() => {
    const fetchClientModules = async () => {
      if (!companyData.client_uuid) {
        setSelectedModuleIds([]);
        return;
      }

      try {
        const { data: moduleData, error } = await supabase
          .from("client_module")
          .select("module_uuid")
          .eq("client_uuid", companyData.client_uuid);

        if (error) {
          console.error("Error fetching client modules:", error);
          return;
        }

        const assignedModuleIds = (moduleData || []).map(
          (cm) => cm.module_uuid
        );
        setSelectedModuleIds(assignedModuleIds);
      } catch (error) {
        console.error("Error fetching client modules:", error);
      }
    };

    fetchClientModules();
  }, [companyData.client_uuid]);

  const fetchCompanyData = async () => {
    try {
      console.log("Fetching Company Data");
      // Check if we have a selected client from the clients list
      const selectedClient = JSON.parse(localStorage.getItem("selectedClient"));
      console.log(selectedClient)
      if (selectedClient) {
        setIsFromClientsList(true);
        setIsNewClient(selectedClient.isNew || false);
        setSaveStatus({ show: false, success: false, message: "" });
        setCompanyData({
          company_name: selectedClient.company_name || "",
          address: selectedClient.address || "",
          city: selectedClient.city || "",
          country: selectedClient.country || "",
          postal_code: selectedClient.postal_code || "",
          email: selectedClient.email || "",
          created_at: selectedClient.created_at || new Date().toISOString(),
          dealer_uuid: selectedClient.dealer_uuid || null,
          client_uuid: selectedClient.client_uuid || null,
        });
        localStorage.removeItem("selectedClient");
        setLoading(false);
        return;
      }

      // If no selected client, proceed with normal flow
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (!currentUser) {
        console.error("No user found");
        return;
      }

      if (currentUser.user_role === "GLOBAL") {
        // Fetch dealer information
        console.log("Fetch dealer information");
        const url = `${serverHost}/dealer_data`;
        const resp = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            dealer_uuid: currentUser.dealer_uuid,
          }),
        });
        if (!resp.ok) {
          console.error("Failed to fetch dealer, ", resp.status);
          return;
        }
        const dealerData = (await resp.json()).data;
        console.log(dealerData);
        if (dealerData) {
          setCompanyData({
            company_name: dealerData.company_name || "",
            address: dealerData.address || "",
            city: dealerData.city || "",
            country: dealerData.country || "",
            postal_code: dealerData.postal_code || "",
            email: dealerData.email || "",
            created_at: dealerData.created_at || "",
            dealer_uuid: dealerData.dealer_uuid,
          });
        }
      } else if (
        currentUser.user_role === "SUPERADMIN" ||
        currentUser.user_role === "ADMIN" ||
        currentUser.user_role === "MGNT"
      ) {
        // For SUPERADMIN, ADMIN, MGNT fetch client information
        const resp = await fetch(`${serverHost}/fetch_client`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            client_uuid: currentUser.client_uuid,
          }),
        });
        if (!resp.ok) {
          const error = await resp.text();
          console.error("Update failed: ", error);
        }
        const clientData = (await resp.json()).data;
        console.log(clientData)
        if (clientData) {
          setCompanyData({
            company_name: clientData.company_name || "",
            address: clientData.address || "",
            city: clientData.city || "",
            country: clientData.country || "",
            postal_code: clientData.postal_code || "",
            email: clientData.email || "",
            created_at: clientData.created_at || "",
            client_uuid: clientData.client_uuid,
            dealer_uuid: clientData.dealer_uuid,
          });
        }
      } else {
        // Default fallback: fetch client information
        const resp = await fetch(`${serverHost}/fetch_client`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            client_uuid: currentUser.client_uuid,
          }),
        });
        if (!resp.ok) {
          const error = await resp.text();
          console.error("Update failed: ", error);
        }
        const clientData = (await resp.json()).data;

        if (clientData) {
          setCompanyData({
            company_name: clientData.company_name || "",
            address: clientData.address || "",
            city: clientData.city || "",
            country: clientData.country || "",
            postal_code: clientData.postal_code || "",
            created_at: clientData.created_at || "",
            client_uuid: clientData.client_uuid,
            dealer_uuid: clientData.dealer_uuid,
          });
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setCompanyData((prev) => ({
      ...prev,
      [id.replace("input-", "")]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    console.log("handleSave called");
    console.log("companyData:", companyData);
    console.log("isNewClient:", isNewClient);
    console.log("isFromClientsList:", isFromClientsList);
    console.log("selectedModuleIds:", selectedModuleIds);

    // Validate required fields
    if (!companyData.company_name) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Company name is required",
      });
      return;
    }
    if (!companyData.email) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Email is required",
      });
      return;
    }

    try {
      let error;
      if (isNewClient) {
        console.log("Creating new client...");
        // Create new client
        const new_client = await fetch(`${serverHost}/register_client`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            company_name: companyData.company_name,
            address: companyData.address,
            city: companyData.city,
            country: companyData.country,
            postal_code: companyData.postal_code,
            email: companyData.email,
            dealer_uuid: companyData.dealer_uuid,
          }),
        });

        if (new_client.status == 200) console.log("Insertion Successful");
        else { //This is
          console.error(new_client.error);
        }
      } else if (
        isFromClientsList ||
        userRole === "SUPERADMIN" ||
        userRole === "ADMIN" ||
        userRole === "MGNT"
      ) {
        console.log("Updating client information...");
        // Update client information for these roles
        const updated_client = await fetch(`${serverHost}/update_client`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(companyData),
        })
        if (updated_client.status == 200) console.log("Insertion Successful");
        else { //This is
          console.error(updated_client.error);
        }
      } else {
        console.log("Updating dealer information...");
        // Update dealer information for GLOBAL role
        const { error: dealerError } = await supabase
          .from("dealer")
          .update({
            company_name: companyData.company_name,
            address: companyData.address,
            city: companyData.city,
            country: companyData.country,
            postal_code: companyData.postal_code,
            email: companyData.email,
          })
          .eq("dealer_uuid", companyData.dealer_uuid);
        console.log("Update dealer error:", dealerError);
        error = dealerError;
      }

      if (error) {
        console.log("Error detected:", error);
        setSaveStatus({
          show: true,
          success: false,
          message:
            `Error ${isNewClient ? "creating" : "updating"} company profile: ` +
            error.message,
        });
        return;
      }

      setSaveStatus({
        show: true,
        success: true,
        message: `Company profile ${
          isNewClient ? "created" : "updated"
        } successfully!`,
      });

      console.log("Save successful");

      // If we came from clients list or created a new client, navigate back after successful save
      if (isFromClientsList || isNewClient) {
        setTimeout(() => {
          navigate("/admin/companyclients");
        }, 1500);
      }
    } catch (error) {
      setSaveStatus({
        show: true,
        success: false,
        message:
          `Error ${isNewClient ? "creating" : "updating"} company profile: ` +
          error.message,
      });
    }
  };

  const handleBack = () => {
    navigate("/admin/companyclients");
  };

  if (loading) {
    return (
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="text-center">Loading...</div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      <SimpleHeader
        name={isNewClient ? "New Client" : "Company Profile"}
        parentName="Company"
      />
      <Container className="mt--6" fluid>
        {saveStatus.show && (
          <Alert
            color={saveStatus.success ? "success" : "danger"}
            isOpen={saveStatus.show}
            toggle={() => setSaveStatus({ ...saveStatus, show: false })}
          >
            {saveStatus.message}
          </Alert>
        )}
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card>
              <CardHeader
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
              >
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">
                      {isNewClient
                        ? "New Client Information"
                        : "Company Information"}
                    </h3>
                  </Col>
                  <Col className="text-right" xs="4">
                    {(isFromClientsList || isNewClient) && (
                      <Button
                        color="light"
                        onClick={handleBack}
                        size="sm"
                        className="mr-2"
                      >
                        Back to Clients
                      </Button>
                    )}
                    <Button color="info" onClick={handleSave} size="sm">
                      Save
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSave}>
                  <h6 className="heading-small text-muted mb-4">
                    Company details
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-company-name"
                          >
                            Company Name <span style={{color: "red"}}>*</span>
                          </label>
                          <Input
                            id="input-company_name"
                            value={companyData.company_name}
                            onChange={handleInputChange}
                            placeholder="Company name"
                            type="text"
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-email"
                          >
                            Email <span style={{color: "red"}}>*</span>
                          </label>
                          <Input
                            id="input-email"
                            value={companyData.email}
                            onChange={handleInputChange}
                            placeholder="Email"
                            type="email"
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-address"
                          >
                            Address
                          </label>
                          <Input
                            id="input-address"
                            value={companyData.address}
                            onChange={handleInputChange}
                            placeholder="Address"
                            type="text"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-city"
                          >
                            City
                          </label>
                          <Input
                            id="input-city"
                            value={companyData.city}
                            onChange={handleInputChange}
                            placeholder="City"
                            type="text"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-country"
                          >
                            Country
                          </label>
                          <Input
                            id="input-country"
                            value={companyData.country}
                            onChange={handleInputChange}
                            placeholder="Country"
                            type="text"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-postal-code"
                          >
                            Postal Code
                          </label>
                          <Input
                            id="input-postal_code"
                            value={companyData.postal_code}
                            onChange={handleInputChange}
                            placeholder="Postal code"
                            type="text"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    {!isNewClient && (
                      <Row>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-created-at"
                            >
                              Created At
                            </label>
                            <Input
                              id="input-created_at"
                              value={new Date(
                                companyData.created_at
                              ).toLocaleDateString()}
                              disabled
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    )}
                    {/* Upload photo section */}
                    {!isNewClient && companyData.client_uuid && (
                      <Row className="mt-4">
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="customFileLang"
                            >
                              Upload Photo
                            </label>
                            <div className="custom-file">
                              <input
                                className="custom-file-input"
                                id="customFileLang"
                                lang="en"
                                type="file"
                                onChange={handleFileChange}
                                style={{ background: "transparent" }}
                              />
                              <label
                                className="custom-file-label"
                                htmlFor="customFileLang"
                              >
                                {selectedFileName || "Select file"}
                              </label>
                            </div>
                          </FormGroup>
                          <Button color="info" size="sm" onClick={uploadPhoto}>
                            Upload Photo
                          </Button>
                          {photoUrl && (
                            <div className="mt-3">
                              <p>Uploaded Photo Preview:</p>
                              <img
                                src={photoUrl}
                                alt="Photo Preview"
                                className="rounded-circle"
                                style={{ width: "100px", height: "100px" }}
                              />
                            </div>
                          )}
                        </Col>
                      </Row>
                    )}
                  </div>
                  {/* Only show modules section for clients */}
                  {companyData.client_uuid && userRole === "GLOBAL" && (
                    <>
                      <hr className="my-4" />
                      <h6 className="heading-small text-muted mb-4">
                        Available Modules
                      </h6>
                      <div className="pl-lg-4">
                        <Row>
                          {allModules.map((module) => (
                            <Col
                              lg="6"
                              key={module.module_uuid}
                              className="mb-3"
                            >
                              <FormGroup
                                check
                                className="d-flex align-items-start p-3 border rounded"
                              >
                                <Input
                                  type="checkbox"
                                  id={`module-${module.module_uuid}`}
                                  checked={selectedModuleIds.includes(
                                    module.module_uuid
                                  )}
                                  onChange={() => {
                                    if (
                                      selectedModuleIds.includes(
                                        module.module_uuid
                                      )
                                    ) {
                                      setSelectedModuleIds(
                                        selectedModuleIds.filter(
                                          (id) => id !== module.module_uuid
                                        )
                                      );
                                    } else {
                                      setSelectedModuleIds([
                                        ...selectedModuleIds,
                                        module.module_uuid,
                                      ]);
                                    }
                                  }}
                                  style={{
                                    marginTop: "0.4rem",
                                    transform: "scale(1.2)",
                                    cursor: "pointer",
                                    marginLeft: "auto",
                                  }}
                                />
                                <img
                                  alt={`${module.name} icon`}
                                  src={require(`../../../assets/img/theme/${module.name.toLowerCase()}-icon.jpg`)}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = require("../../../assets/img/theme/bootstrap.jpg");
                                  }}
                                  style={{
                                    width: "48px",
                                    height: "48px",
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                    marginLeft: "15px",
                                    marginRight: "15px",
                                  }}
                                />
                                <div>
                                  <label
                                    htmlFor={`module-${module.module_uuid}`}
                                    className="form-control-label mb-1"
                                    style={{ cursor: "pointer" }}
                                  >
                                    {module.name}
                                  </label>
                                  <div
                                    className="text-muted"
                                    style={{ maxWidth: "400px" }}
                                  >
                                    {module.description}
                                  </div>
                                </div>
                              </FormGroup>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </>
                  )}
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default CompanyProfile;
